import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Clock, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Navigation,
  Loader2,
  Users,
  UserCheck,
  UserX,
  Search,
  RefreshCw,
  Calendar,
  BarChart3,
  Download,
  Eye,
  Check,
  X,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";

const API_URL = '';

export default function TeacherAttendanceDashboard() {
  const [searchParams] = useSearchParams();
  const schoolFilter = searchParams.get('school');
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ stats: {}, teachers: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [absentDialogOpen, setAbsentDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [adminComment, setAdminComment] = useState("");
  const [verifyNotes, setVerifyNotes] = useState("");
  const [absentReason, setAbsentReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState("today");
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchTodayAttendance();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    // Auto-refresh every 30 seconds
    const refreshTimer = setInterval(() => {
      fetchTodayAttendance(true);
    }, 30000);
    
    return () => {
      clearInterval(timer);
      clearInterval(refreshTimer);
    };
  }, [schoolFilter]);

  useEffect(() => {
    if (activeTab === "report") {
      fetchMonthlyReport();
    }
  }, [activeTab]);

  const fetchTodayAttendance = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      // Build URL with optional school filter
      let url = `${API_URL}/api/teacher-attendance/today`;
      if (schoolFilter) {
        url += `?school_filter=${encodeURIComponent(schoolFilter)}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      if (!silent) toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      setReportData(result);
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  };

  const handleApproveAll = async () => {
    const pending = (data?.teachers || []).filter(t => t.sign_in_time && t.verification_status === "pending").length;
    if (!pending || !window.confirm(`Approve all ${pending} pending sign-ins for today?`)) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/verify-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Approved ${result.approved} sign-in${result.approved === 1 ? "" : "s"}`);
        fetchTodayAttendance(true);
      } else {
        toast.error(result.detail || "Failed to approve");
      }
    } catch (error) {
      toast.error("Failed to approve attendance");
    } finally {
      setProcessing(false);
    }
  };

  const handleVerify = async (status) => {
    if (!selectedTeacher?.attendance_id) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attendance_id: selectedTeacher.attendance_id,
          status: status,
          notes: verifyNotes
        })
      });

      if (response.ok) {
        toast.success(status === "verified" ? "Attendance approved" : "Attendance rejected");
        setVerifyDialogOpen(false);
        setVerifyNotes("");
        setSelectedTeacher(null);
        fetchTodayAttendance(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to verify");
      }
    } catch (error) {
      toast.error("Failed to verify attendance");
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveComment = async () => {
    if (!selectedTeacher) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/admin-comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          date: data?.date || new Date().toISOString().split('T')[0],
          comment: adminComment
        })
      });
      if (response.ok) {
        toast.success(adminComment.trim() ? "Comment saved" : "Comment removed");
        setCommentDialogOpen(false);
        setAdminComment("");
        setSelectedTeacher(null);
        fetchTodayAttendance(true);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to save comment");
      }
    } catch (error) {
      toast.error("Failed to save comment");
    } finally {
      setProcessing(false);
    }
  };

  const openCommentDialog = (teacher) => {
    setSelectedTeacher(teacher);
    setAdminComment(teacher.admin_comment || "");
    setCommentDialogOpen(true);
  };

  const needsComment = (teacher) => !teacher.sign_in_time || teacher.is_late || teacher.verification_status === "absent";

  const handleMarkAbsent = async () => {
    if (!selectedTeacher || !absentReason) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const today = data?.date || new Date().toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/teacher-attendance/mark-absent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          date: today,
          reason: absentReason
        })
      });

      if (response.ok) {
        toast.success("Teacher marked as absent");
        setAbsentDialogOpen(false);
        setAbsentReason("");
        setSelectedTeacher(null);
        fetchTodayAttendance(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to mark absent");
      }
    } catch (error) {
      toast.error("Failed to mark absent");
    } finally {
      setProcessing(false);
    }
  };

  const handleResetTeacherAttendance = async (teacher) => {
    if (!confirm(`Reset attendance for ${teacher.name}? This will allow them to sign in again.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const today = new Date().toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/teacher-attendance/admin-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teacher_id: teacher.id,
          date: today
        })
      });

      if (response.ok) {
        toast.success(`Attendance reset for ${teacher.name}. They can sign in again.`);
        fetchTodayAttendance(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to reset attendance");
      }
    } catch (error) {
      toast.error("Failed to reset attendance");
    }
  };

  // Vanuatu/Solomon Islands timezone: UTC+11 hours
  const VANUATU_OFFSET_HOURS = 11;
  const VANUATU_OFFSET_MS = VANUATU_OFFSET_HOURS * 60 * 60 * 1000;
  
  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    
    // Ensure we parse as UTC by adding 'Z' if not present
    let utcString = isoString;
    if (!isoString.endsWith('Z') && !isoString.includes('+')) {
      utcString = isoString + 'Z';
    }
    
    // Parse the UTC time from database
    const utcDate = new Date(utcString);
    
    // Convert to Vanuatu time (UTC+11) by adding offset
    const vanuatuMs = utcDate.getTime() + VANUATU_OFFSET_MS;
    const vanuatuDate = new Date(vanuatuMs);
    
    // Extract hours and minutes from the adjusted time
    const hours = vanuatuDate.getUTCHours();
    const minutes = vanuatuDate.getUTCMinutes();
    
    // Determine AM/PM based on 24-hour value
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format (12 stays as 12, 0 becomes 12)
    const hour12 = hours % 12 || 12;
    
    // Format with leading zeros
    return `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const getLocationIcon = (locationStatus, distance, isOnSchoolNetwork, networkLocationStatus) => {
    // Prioritize network-based location for on-site detection
    if (isOnSchoolNetwork) {
      return <Badge className="bg-green-100 text-green-800 text-xs"><MapPin className="w-3 h-3 mr-1" /> On-Site (Network)</Badge>;
    }
    
    switch (locationStatus) {
      case "at_school":
        return <Badge className="bg-green-100 text-green-800 text-xs"><MapPin className="w-3 h-3 mr-1" /> At School</Badge>;
      case "nearby":
        return <Badge className="bg-yellow-100 text-yellow-800 text-xs"><MapPin className="w-3 h-3 mr-1" /> {distance}m</Badge>;
      case "off_campus":
        return <Badge className="bg-red-100 text-red-800 text-xs"><AlertTriangle className="w-3 h-3 mr-1" /> {distance ? `${(distance/1000).toFixed(1)}km away` : 'Off Campus'}</Badge>;
      case "unknown":
        return <Badge className="bg-gray-100 text-gray-600 text-xs"><MapPin className="w-3 h-3 mr-1" /> Remote</Badge>;
      default:
        return null;
    }
  };

  const getStatusBadge = (teacher) => {
    if (teacher.verification_status === "absent") {
      return <Badge className="bg-gray-100 text-gray-800">Absent</Badge>;
    }
    if (!teacher.sign_in_time) {
      return <Badge className="bg-red-100 text-red-800">Not Signed In</Badge>;
    }
    if (teacher.verification_status === "verified") {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
    }
    if (teacher.verification_status === "rejected") {
      return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
  };

  // Filter teachers
  const filteredTeachers = data.teachers?.filter(teacher => {
    const matchesSearch = teacher.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "signed_in") return matchesSearch && teacher.sign_in_time;
    if (filterStatus === "not_signed_in") return matchesSearch && !teacher.sign_in_time;
    if (filterStatus === "pending") return matchesSearch && teacher.verification_status === "pending";
    if (filterStatus === "late") return matchesSearch && teacher.is_late;
    if (filterStatus === "off_campus") return matchesSearch && teacher.location_status === "off_campus" && !teacher.is_on_school_network;
    
    return matchesSearch;
  }) || [];

  // Use Vanuatu timezone for consistent time display (UTC+11)
  const getVanuatuTime = () => {
    const now = new Date();
    const vanuatuDate = new Date(now.getTime() + (11 * 60 * 60 * 1000));
    let hours = vanuatuDate.getUTCHours();
    const minutes = vanuatuDate.getUTCMinutes();
    if (hours >= 24) hours -= 24;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };
  const timeString = getVanuatuTime();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="teacher-attendance-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {schoolFilter === 'school-stpatricks-college' ? 'Senior Teacher Attendance' : 'Teacher Attendance'}
          </h1>
          <p className="text-gray-600">
            {schoolFilter === 'school-stpatricks-college' && (
              <Badge className="mr-2 bg-blue-100 text-blue-800">St Patrick's College</Badge>
            )}
            {data.date} • Current Time: {timeString}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fetchTodayAttendance()}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="today">Today's Attendance</TabsTrigger>
          <TabsTrigger value="report">Monthly Report</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-700">Total</span>
                </div>
                <p className="text-2xl font-bold text-blue-800 mt-1">{data.stats?.total || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">Signed In</span>
                </div>
                <p className="text-2xl font-bold text-green-800 mt-1">{data.stats?.signed_in || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <UserX className="w-5 h-5 text-red-600" />
                  <span className="text-sm text-red-700">Not Signed In</span>
                </div>
                <p className="text-2xl font-bold text-red-800 mt-1">{data.stats?.not_signed_in || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm text-yellow-700">Pending</span>
                </div>
                <p className="text-2xl font-bold text-yellow-800 mt-1">{data.stats?.pending_verification || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <span className="text-sm text-orange-700">Late</span>
                </div>
                <p className="text-2xl font-bold text-orange-800 mt-1">{data.stats?.late || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-purple-700">On-Site</span>
                </div>
                <p className="text-2xl font-bold text-purple-800 mt-1">{data.stats?.on_school_network || data.stats?.at_school || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search teachers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "all", label: "All" },
                { value: "signed_in", label: "Signed In" },
                { value: "not_signed_in", label: "Not Signed In" },
                { value: "pending", label: "Pending" },
                { value: "late", label: "Late" },
                { value: "off_campus", label: "Off Campus" }
              ].map(filter => (
                <Button
                  key={filter.value}
                  variant={filterStatus === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterStatus(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Teachers List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Teachers ({filteredTeachers.length})</CardTitle>
              {(data?.teachers || []).some(t => t.sign_in_time && t.verification_status === "pending") && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleApproveAll}
                  disabled={processing}
                  data-testid="approve-all-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                  Approve All Pending ({(data?.teachers || []).filter(t => t.sign_in_time && t.verification_status === "pending").length})
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Teacher</th>
                      <th className="text-left p-3 font-medium">Sign In</th>
                      <th className="text-left p-3 font-medium">Location</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Sign Out</th>
                      <th className="text-left p-3 font-medium">Hours</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeachers.map((teacher) => (
                      <tr key={teacher.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{teacher.name}</p>
                            <p className="text-sm text-gray-500">{teacher.department || "Teacher"}</p>
                            {teacher.school_name && teacher.school_id !== data.teachers?.[0]?.school_id && (
                              <Badge className="mt-1 bg-blue-100 text-blue-800 text-xs">
                                {teacher.school_name.includes('Junior') ? 'Junior' : teacher.school_name}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={teacher.is_late ? "text-orange-600 font-medium" : ""}>
                            {formatTime(teacher.sign_in_time)}
                          </span>
                          {teacher.is_late && (
                            <Badge className="ml-2 bg-orange-100 text-orange-800 text-xs">Late</Badge>
                          )}
                          {teacher.is_late && teacher.notes?.startsWith('Late reason:') && (
                            <p className="mt-1 text-xs text-orange-700 italic max-w-[200px]" data-testid={`late-reason-${teacher.id}`}>{teacher.notes.replace('Late reason: ', '')}</p>
                          )}
                        </td>
                        <td className="p-3">
                          {getLocationIcon(teacher.location_status, teacher.distance_meters, teacher.is_on_school_network, teacher.network_location_status)}
                        </td>
                        <td className="p-3">
                          {getStatusBadge(teacher)}
                          {teacher.verification_status === "absent" && teacher.absence_reason && (
                            <p className="mt-1 text-xs text-gray-500 italic" data-testid={`absence-reason-${teacher.id}`}>{teacher.absence_reason}</p>
                          )}
                          {teacher.admin_comment && teacher.admin_comment !== teacher.absence_reason && (
                            <p className="mt-1 text-xs text-blue-700 flex items-start gap-1 max-w-[220px]" data-testid={`admin-comment-${teacher.id}`}>
                              <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>{teacher.admin_comment}{teacher.admin_comment_by ? <span className="text-gray-400"> — {teacher.admin_comment_by}</span> : null}</span>
                            </p>
                          )}
                        </td>
                        <td className="p-3">
                          {formatTime(teacher.sign_out_time)}
                        </td>
                        <td className="p-3">
                          {teacher.hours_worked ? `${teacher.hours_worked}h` : "--"}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-2">
                            {teacher.sign_in_time && teacher.verification_status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => {
                                    setSelectedTeacher(teacher);
                                    setVerifyDialogOpen(true);
                                  }}
                                  data-testid={`verify-btn-${teacher.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              </>
                            )}
                            {!teacher.sign_in_time && teacher.verification_status !== "absent" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-gray-600 hover:text-gray-700"
                                onClick={() => {
                                  setSelectedTeacher(teacher);
                                  setAbsentDialogOpen(true);
                                }}
                                data-testid={`absent-btn-${teacher.id}`}
                              >
                                Mark Absent
                              </Button>
                            )}
                            {needsComment(teacher) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className={teacher.admin_comment ? "text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}
                                onClick={() => openCommentDialog(teacher)}
                                data-testid={`comment-btn-${teacher.id}`}
                                title={teacher.admin_comment ? "Edit comment" : "Add comment"}
                              >
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Reset button for teachers who completed attendance (signed in AND signed out) */}
                            {teacher.sign_in_time && teacher.sign_out_time && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                onClick={() => handleResetTeacherAttendance(teacher)}
                                data-testid={`reset-btn-${teacher.id}`}
                                title="Reset attendance (allow re-sign in)"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredTeachers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No teachers found matching your criteria
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="report" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Monthly Attendance Report
              </CardTitle>
              <CardDescription>
                {reportData ? `${new Date(0, reportData.month - 1).toLocaleString('default', { month: 'long' })} ${reportData.year}` : 'Loading...'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData?.teachers?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-medium">Teacher</th>
                        <th className="text-center p-3 font-medium">Days Present</th>
                        <th className="text-center p-3 font-medium">Days Absent</th>
                        <th className="text-center p-3 font-medium">Days Late</th>
                        <th className="text-center p-3 font-medium">Avg Hours</th>
                        <th className="text-center p-3 font-medium">On-Site Sign-ins</th>
                        <th className="text-center p-3 font-medium">Off-Campus Sign-ins</th>
                        <th className="text-left p-3 font-medium">Comments & Reasons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.teachers.map((teacher) => (
                        <tr key={teacher.id} className="border-b hover:bg-gray-50 align-top">
                          <td className="p-3">
                            <p className="font-medium">{teacher.name}</p>
                            <p className="text-sm text-gray-500">{teacher.department || "Teacher"}</p>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-green-600 font-medium">{teacher.days_present}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={teacher.days_absent > 3 ? "text-red-600 font-medium" : "text-gray-600"}>
                              {teacher.days_absent}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={teacher.days_late > 3 ? "text-orange-600 font-medium" : "text-gray-600"}>
                              {teacher.days_late}
                            </span>
                          </td>
                          <td className="p-3 text-center">{teacher.avg_hours}h</td>
                          <td className="p-3 text-center">
                            {teacher.on_site_signins > 0 && (
                              <Badge className="bg-green-100 text-green-800">{teacher.on_site_signins}</Badge>
                            )}
                            {teacher.on_site_signins === 0 && <span className="text-gray-400">0</span>}
                          </td>
                          <td className="p-3 text-center">
                            {teacher.off_campus_signins > 0 && (
                              <Badge className="bg-yellow-100 text-yellow-800">{teacher.off_campus_signins}</Badge>
                            )}
                            {teacher.off_campus_signins === 0 && <span className="text-gray-400">0</span>}
                          </td>
                          <td className="p-3 min-w-[260px]" data-testid={`report-notes-${teacher.id}`}>
                            {teacher.notes?.length ? (
                              <ul className="space-y-1.5">
                                {teacher.notes.map((n) => (
                                  <li key={n.date} className="text-xs flex gap-2">
                                    <span className="text-gray-500 shrink-0 w-12">{new Date(n.date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                    <Badge className={`shrink-0 h-5 px-1.5 text-[10px] ${n.type === 'absent' ? 'bg-red-100 text-red-800' : n.type === 'late' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-700'}`}>
                                      {n.type === 'absent' ? 'Absent' : n.type === 'late' ? 'Late' : n.type === 'not_signed_in' ? 'No sign-in' : 'Note'}
                                    </Badge>
                                    <span className="text-gray-700">
                                      {n.absence_reason && <span>{n.absence_reason}</span>}
                                      {n.late_reason && <span className="italic">{n.late_reason}</span>}
                                      {n.admin_comment && (
                                        <span className="text-blue-700">{(n.absence_reason || n.late_reason) ? ' · ' : ''}{n.admin_comment}{n.admin_comment_by ? <span className="text-gray-400"> — {n.admin_comment_by}</span> : null}</span>
                                      )}
                                      {!n.absence_reason && !n.late_reason && !n.admin_comment && <span className="text-gray-400">No reason recorded</span>}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No attendance data available for this month
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Verify Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Attendance</DialogTitle>
            <DialogDescription>
              Approve {selectedTeacher?.name}'s sign-in for today
            </DialogDescription>
          </DialogHeader>
          
          {selectedTeacher && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sign In Time:</span>
                  <span className="font-medium">{formatTime(selectedTeacher.sign_in_time)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  {getLocationIcon(selectedTeacher.location_status, selectedTeacher.distance_meters, selectedTeacher.is_on_school_network)}
                </div>
                {selectedTeacher.is_late && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <Badge className="bg-orange-100 text-orange-800">Late</Badge>
                  </div>
                )}
                {selectedTeacher.is_late && selectedTeacher.notes?.startsWith('Late reason:') && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600 shrink-0">Late reason:</span>
                    <span className="text-sm text-orange-700 italic text-right" data-testid="approve-late-reason">{selectedTeacher.notes.replace('Late reason: ', '')}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Notes (optional)</label>
                <Textarea
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                  placeholder="Add any notes..."
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setVerifyDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => handleVerify("rejected")}
              disabled={processing}
            >
              <X className="w-4 h-4 mr-1" />
              Reject
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleVerify("verified")}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Absent Dialog */}
      <Dialog open={absentDialogOpen} onOpenChange={setAbsentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Teacher Absent</DialogTitle>
            <DialogDescription>
              Mark {selectedTeacher?.name} as absent for today
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <label className="text-sm font-medium">Reason for Absence</label>
            <Textarea
              value={absentReason}
              onChange={(e) => setAbsentReason(e.target.value)}
              placeholder="Enter reason for absence..."
              className="mt-1"
              required
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAbsentDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkAbsent}
              disabled={processing || !absentReason}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Mark Absent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent data-testid="admin-comment-dialog">
          <DialogHeader>
            <DialogTitle>Attendance Comment</DialogTitle>
            <DialogDescription>
              Add a note about {selectedTeacher?.name}'s attendance today
              {selectedTeacher?.is_late ? " (signed in late)" : !selectedTeacher?.sign_in_time ? " (not signed in)" : ""}
            </DialogDescription>
          </DialogHeader>

          <div>
            <label className="text-sm font-medium">Comment</label>
            <Textarea
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              placeholder="e.g. Called in sick, attending workshop, late due to transport..."
              className="mt-1"
              rows={4}
              data-testid="admin-comment-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentDialogOpen(false)} data-testid="admin-comment-cancel">
              Cancel
            </Button>
            <Button onClick={handleSaveComment} disabled={processing} data-testid="admin-comment-save">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
